<template>
  <main
    class="campaign-area campaign-report"
    :dir="uiLocale === 'en' ? 'ltr' : 'rtl'"
  >
    <header><h1>{{ words.reports }}</h1><p>{{ words.reportHint }}</p><small>{{ words.reportTimezone }}</small></header>
    <form
      class="campaign-panel campaign-grid"
      @submit.prevent="load"
    >
      <CampaignReportDate
        :value="from"
        :locale="uiLocale"
        :label="words.from"
        field="from"
        @change="from = $event"
      /><CampaignReportDate
        :value="to"
        :locale="uiLocale"
        :label="words.to"
        field="to"
        @change="to = $event"
      /><RulePicker
        :value="campaignId ? [campaignId] : []"
        kind="campaigns"
        :title="words.title"
        :locale="locale || uiLocale"
        :maximum="1"
        report-context
        @input="campaignId = $event[0] || ''"
      /><label>{{ words.language }}<select v-model="locale"><option value="">{{ words.reportAll }}</option><option value="fa">فارسی</option><option value="en">English</option><option value="ar">العربية</option></select></label><button
        class="primary"
        :disabled="loading"
      >
        {{ words.filter }}
      </button><button
        v-if="$can('export', 'contentCampaign')"
        type="button"
        :disabled="loading || !canExport"
        @click="exportCsv"
      >
        {{ words.export }}
      </button>
      <details class="campaign-report-filters"><summary>{{ words.advanced }}</summary><div class="campaign-grid"><RulePicker
        :value="contentId ? [contentId] : []"
        kind="content"
        :title="words.contentIds"
        :locale="locale || uiLocale"
        :maximum="1"
        report-context
        @input="contentId = $event[0] || ''"
      /><label>{{ words.device }}<select v-model="device"><option value="">{{ words.reportAll }}</option><option value="mobile">{{ words.mobile }}</option><option value="tablet">{{ words.tablet }}</option><option value="desktop">{{ words.desktop }}</option><option value="unknown">{{ words.unknown }}</option></select></label><label>{{ words.template }}<select v-model="template"><option value="">{{ words.reportAll }}</option><option value="image-banner">{{ words.image }}</option><option value="text-card">{{ words.text }}</option><option value="compact-strip">{{ words.compact }}</option></select></label><label>{{ words.position }}<select v-model="placement"><option value="">{{ words.reportAll }}</option><option value="automatic">{{ words.automatic }}</option><option value="end">{{ words.endPosition }}</option></select></label><label>{{ words.revision }}<input
        v-model="revision"
        type="number"
        min="1"
      ></label><label>{{ words.action }}<select v-model="actionId"><option value="">{{ words.reportAll }}</option><option value="primary">{{ words.action }}</option><option value="secondary">{{ words.secondary }}</option></select></label></div></details>
    </form>
    <p
      v-if="message"
      class="campaign-message"
      role="alert"
    >
      {{ message }}
    </p><p
      v-if="loading"
      role="status"
    >
      {{ words.loading }}
    </p>
    <template v-if="report">
      <section
        class="campaign-panel"
        role="status"
        aria-live="polite"
      >
        <h2>{{ words.coverage }}</h2>
        <p>{{ coverageMessage }}</p>
        <p v-if="summaryCoverage && summaryCoverage.missingDays.length">
          {{ words.missingDays }}: <bdi dir="ltr">{{ summaryCoverage.missingDays.join(', ') }}</bdi>
        </p>
        <p>{{ words.summaryTotalsHint }}</p><p>{{ words.conversion }}</p>
        <p v-if="!canExport && $can('export', 'contentCampaign')">
          {{ words.exportIncomplete }}
        </p>
      </section><p
        v-if="statsNotice"
        :class="statsNotice === words.statsRefresh ? 'campaign-info' : 'campaign-message'"
        role="status"
      >{{ statsNotice }}</p><p
        v-if="report.freshness.actionCoverage === 'unavailable'"
        class="campaign-message"
        role="status"
      >
        {{ words.actionCoverageUnavailable }}
      </p>
      <div class="campaign-metrics">
        <div class="campaign-panel">
          {{ words.impressions }}<strong>{{ metricsAvailable ? count(report.totals.impressions) : words.unknown }}</strong>
        </div><div class="campaign-panel">
          {{ words.clicks }}<strong>{{ metricsAvailable ? count(report.totals.clicks) : words.unknown }}</strong>
        </div><div class="campaign-panel">
          {{ words.ctr }}<strong>{{ metricsAvailable ? rate(report.totals.ctr) : words.unknown }}</strong>
        </div>
      </div><p>{{ words.refresh }}: {{ refreshedAt(report.freshness.lastRefreshedAt) }} · {{ words.tehranTime }}</p><p v-if="metricsAvailable && !report.rows.length">
        {{ emptyReportMessage }}
      </p><div
        v-else-if="metricsAvailable"
        class="campaign-panel campaign-table-wrap"
      >
        <table><caption>{{ words.revision }} · {{ words.reports }}</caption><thead><tr><th>{{ words.reportDay }}</th><th>{{ words.name }}</th><th>{{ words.revision }}</th><th>{{ words.contentIds }}</th><th>{{ words.language }}</th><th>{{ words.impressions }}</th><th>{{ words.clicks }}</th><th>{{ words.ctr }}</th></tr></thead><tbody><tr
          v-for="(row, index) in visibleRows"
          :key="index"
        >
          <td>{{ row.day }}</td><td>{{ metadataName('campaigns', row.campaignId) }}</td><td>{{ row.revision }}</td><td>{{ metadataName(row.contentType === 'article-category' ? 'categories' : 'content', row.contentId) }}</td><td>{{ row.locale }}</td><td>{{ count(row.impressions) }}</td><td>{{ row.clicks }}</td><td>{{ rate(row.ctr) }}</td>
        </tr></tbody></table>
      </div>
      <nav
        v-if="metricsAvailable && report.rows.length > reportLimit"
        class="campaign-actions"
        :aria-label="words.page"
      >
        <button
          type="button"
          :disabled="reportPage <= 1"
          @click="reportPage -= 1"
        >
          {{ words.previous }}
        </button><span>{{ words.page }} {{ reportPage }} / {{ reportPages }}</span><button
          type="button"
          :disabled="reportPage >= reportPages"
          @click="reportPage += 1"
        >
          {{ words.next }}
        </button>
      </nav>
      <p v-if="metricsAvailable">
        {{ words.withoutImpression }}: {{ report.totals.clicksWithoutImpression }}
      </p><section
        v-if="metricsAvailable"
        class="campaign-panel"
      >
        <h2>{{ words.trends }}</h2><div
          class="campaign-trend"
          role="img"
          :aria-label="words.trends"
        >
          <div
            v-for="day in trends"
            :key="day.day"
            class="campaign-trend-day"
          >
            <span>{{ day.day }}</span><div
              class="campaign-trend-track"
              :aria-hidden="day.impressions === null"
            >
              <span
                v-if="day.impressions !== null"
                :style="{ width: `${trendWidth(day.impressions)}%` }"
              />
            </div><strong>{{ count(day.impressions) }}</strong>
          </div>
        </div><div
          class="campaign-table-wrap"
          role="region"
          :aria-label="words.trends"
          tabindex="0"
        ><table><thead><tr><th>{{ words.reportDay }}</th><th>{{ words.impressions }}</th><th>{{ words.clicks }}</th></tr></thead><tbody><tr
          v-for="day in trends"
          :key="day.day"
        >
          <td>{{ day.day }}</td><td>{{ count(day.impressions) }}</td><td>{{ day.clicks }}</td>
        </tr></tbody></table></div>
      </section><section
        v-if="metricsAvailable"
        class="campaign-panel"
      >
        <h2>{{ words.topContent }}</h2><ol><li
          v-for="item in topContent"
          :key="(item.contentType || 'article') + item.id"
        >
          {{ metadataName(item.contentType === 'article-category' ? 'categories' : 'content', item.id) }} · {{ words.clicks }} {{ item.clicks }}
        </li></ol>
      </section>
    </template>
    <details class="campaign-panel"><summary>{{ words.advanced }}</summary>    <form
      class="campaign-panel campaign-grid"
      @submit.prevent="reconcile"
    >
      <CampaignReportDate
        :value="reconcileDay"
        :locale="uiLocale"
        :label="words.reconcileDay"
        field="reconcile"
        @change="reconcileDay = $event"
      /><button :disabled="loading">
        {{ words.reconcile }}
      </button>
    </form></details>
  </main>
</template>
<script>
import axios from '@/libs/axios'
import messages from './messages'
import reasonText from './reasons'
import RulePicker from './RulePicker.vue'
import CampaignReportDate from './CampaignReportDate.vue'
import { errorKey } from './model'
import './campaign.css'

export default {
  components: { RulePicker, CampaignReportDate },
  data() {
    const now = new Date(); return {
      from: new Date(now.getTime() - 7 * 86400000).toISOString().slice(0, 10), to: now.toISOString().slice(0, 10), campaignId: '', locale: '', contentId: '', device: '', template: '', placement: '', revision: '', actionId: '', reconcileDay: '', report: null, loading: false, message: '', sequence: 0, applied: null, reportPage: 1, reportLimit: 50, names: { campaigns: {}, content: {}, categories: {} }, nameStates: { campaigns: {}, content: {}, categories: {} }, nameSequence: 0,
    }
  },
  computed: {
    summaryCoverage() {
      const coverage = this.report && this.report.freshness && this.report.freshness.summaryCoverage
      if (!coverage || !['none', 'partial', 'complete'].includes(coverage.status) || coverage.missingDaysAreZero !== false || coverage.totalsScope !== 'available-summary-days-only') return null
      const keys = ['expectedDays', 'availableDays', 'missingDays']
      if (!keys.every(key => Array.isArray(coverage[key]) && coverage[key].every(day => typeof day === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(day) && Number.isFinite(Date.parse(`${day}T00:00:00Z`)) && new Date(`${day}T00:00:00Z`).toISOString().slice(0, 10) === day) && new Set(coverage[key]).size === coverage[key].length)) return null
      const { expectedDays, availableDays, missingDays } = coverage
      if (!expectedDays.length || expectedDays.length > 93 || availableDays.length + missingDays.length !== expectedDays.length || !availableDays.every(day => expectedDays.includes(day) && !missingDays.includes(day)) || !missingDays.every(day => expectedDays.includes(day))) return null
      let status = 'complete'
      if (!availableDays.length) status = 'none'
      else if (missingDays.length) status = 'partial'
      return status === coverage.status ? coverage : null
    },
    canExport() { return this.metricsAvailable && !!this.summaryCoverage && this.summaryCoverage.status === 'complete' },
    coverageMessage() { if (!this.summaryCoverage) return this.words.coverageUnknown; return this.words[`coverage${this.summaryCoverage.status}`] },
    emptyReportMessage() { if (!this.summaryCoverage) return this.words.emptyUnknown; return this.summaryCoverage.status === 'complete' ? this.words.noMetrics : this.words.emptyIncomplete },
    metricsAvailable() { const state = this.report && this.report.freshness; return Boolean(state && (!this.summaryCoverage || this.summaryCoverage.status !== 'none') && state.readiness !== 'unavailable' && state.coveredDays !== 0 && (state.readiness !== 'disabled' || state.coveredDays > 0)) },
    statsNotice() { const state = this.report && this.report.freshness; if (!state) return ''; if (state.readiness === 'disabled') return this.words.statsDisabled; if (state.readiness === 'unavailable') return this.words.statsUnavailable; if (state.coveredDays === 0) return this.words.statsPending; if ((state.missingDays || []).length) return this.words.statsPartial; if ((state.staleDays || []).length) return this.words.statsStale; return state.reconciliation && state.reconciliation.enabled ? this.words.statsRefresh : '' },
    reportLocale() { return (this.applied && this.applied.locale) || this.uiLocale },
    reportPages() { return Math.max(1, Math.ceil(((this.report && this.report.rows) || []).length / this.reportLimit)) },
    visibleRows() { return ((this.report && this.report.rows) || []).slice((this.reportPage - 1) * this.reportLimit, this.reportPage * this.reportLimit) },
    uiLocale() { return this.$i18n.locale },
    words() { return messages[this.uiLocale] || messages.fa },
    trends() { const days = {}; ((this.report && this.report.rows) || []).forEach(row => { if (!days[row.day]) days[row.day] = { day: row.day, impressions: 0, clicks: 0 }; days[row.day].impressions = typeof row.impressions !== 'number' || days[row.day].impressions === null ? null : days[row.day].impressions + row.impressions; days[row.day].clicks += row.clicks }); return Object.values(days).sort((a, b) => a.day.localeCompare(b.day)) },
    topContent() { const items = {}; ((this.report && this.report.rows) || []).forEach(row => { const key = `${row.contentType === 'article-category' ? 'article-category' : 'article'}:${row.contentId}`; if (!items[key]) items[key] = { id: row.contentId, contentType: row.contentType, clicks: 0 }; items[key].clicks += row.clicks }); return Object.values(items).sort((a, b) => b.clicks - a.clicks || a.id.localeCompare(b.id)).slice(0, 10) },
  },
  watch: { '$route.query.campaignId': function selectedCampaignChanged() { this.selectCampaign() }, reportPage() { this.hydrateNames() }, reportLocale() { this.names = { campaigns: {}, content: {}, categories: {} }; this.nameStates = { campaigns: {}, content: {}, categories: {} }; this.hydrateNames() } },
  created() { this.selectCampaign() },
  beforeDestroy() { this.sequence += 1; this.nameSequence += 1 },
  methods: {
    selectCampaign() { const value = this.$route.query && this.$route.query.campaignId; this.campaignId = typeof value === 'string' && /^[a-f0-9]{24}$/i.test(value) ? value : ''; if (this.$can('report', 'contentCampaign')) this.load() },
    refreshedAt(value) { if (!value) return this.words.unknown; const date = new Date(value); return Number.isFinite(date.getTime()) ? date.toLocaleString(this.uiLocale, { timeZone: 'Asia/Tehran' }) : this.words.unknown },
    metadataName(kind, id) { if (this.names[kind][id]) return this.names[kind][id]; if (this.nameStates[kind][id] === 'failed') return this.words.lookupFailed; if (this.nameStates[kind][id] === 'unavailable') return this.words.optionUnavailable; return this.words.loading },
    async hydrateNames() {
      this.nameSequence += 1; const sequence = this.nameSequence; if (!this.report) return
      const scopes = { campaigns: [...new Set(this.visibleRows.map(row => row.campaignId))], content: [...new Set([...this.visibleRows.filter(row => row.contentType !== 'article-category').map(row => row.contentId), ...this.topContent.filter(item => item.contentType !== 'article-category').map(item => item.id)])], categories: [...new Set([...this.visibleRows.filter(row => row.contentType === 'article-category').map(row => row.contentId), ...this.topContent.filter(item => item.contentType === 'article-category').map(item => item.id)])] }; const jobs = []
      Object.keys(scopes).forEach(kind => { const ids = scopes[kind].filter(id => !this.names[kind][id]); for (let index = 0; index < ids.length; index += 50) { const batch = ids.slice(index, index + 50); batch.forEach(id => this.$set(this.nameStates[kind], id, 'loading')); jobs.push({ kind, ids: batch }) } })
      await Promise.all(jobs.map(async job => {
        try {
          const response = await axios.post('content-campaign-reports/options', {
            kind: job.kind, ids: job.ids, locale: this.reportLocale, page: 1, limit: 50,
          }, { timeout: 10000 }); if (sequence !== this.nameSequence) return; if (!response.data || !Array.isArray(response.data.items)) throw new Error('INVALID_REPORT_OPTIONS'); const items = response.data.items.filter(item => job.ids.includes(item.id) && typeof item.title === 'string' && item.title.trim()); items.forEach(item => this.$set(this.names[job.kind], item.id, item.title)); job.ids.forEach(id => this.$set(this.nameStates[job.kind], id, items.some(item => item.id === id) ? 'ready' : 'unavailable'))
        } catch (error) { if (sequence === this.nameSequence) job.ids.forEach(id => this.$set(this.nameStates[job.kind], id, 'failed')) }
      }))
    },
    count(value) { return typeof value === 'number' && Number.isFinite(value) ? String(value) : this.words.unknown },
    failureText(error) { if (error && error.message === 'INVALID_RANGE') return reasonText('CAMPAIGN_REPORT_RANGE', this.uiLocale); const code = error && error.response && error.response.data && error.response.data.message; if (typeof code === 'string' && code.startsWith('CAMPAIGN_')) return reasonText(code, this.uiLocale); return this.words[errorKey(error)] },
    trendWidth(count) { const maximum = Math.max(1, ...this.trends.map(day => day.impressions).filter(value => typeof value === 'number' && Number.isFinite(value))); return typeof count === 'number' ? (100 * count) / maximum : 0 },
    async reconcile() { if (this.loading || !/^\d{4}-\d{2}-\d{2}$/.test(this.reconcileDay) || !this.$can('report', 'contentCampaign')) return; this.loading = true; try { await axios.post('content-campaign-reports/reconcile', { day: this.reconcileDay }, { timeout: 20000 }); this.loading = false; await this.load() } catch (error) { this.message = this.failureText(error); this.loading = false } },
    rate(value) { return typeof value === 'number' && Number.isFinite(value) ? `${(value * 100).toFixed(1)}%` : this.words.unknown },
    params() { const from = new Date(`${this.from}T00:00:00Z`); const to = new Date(`${this.to}T00:00:00Z`); to.setUTCDate(to.getUTCDate() + 1); if (!Number.isFinite(from.getTime()) || !Number.isFinite(to.getTime()) || to <= from || to - from > 31 * 86400000) throw new Error('INVALID_RANGE'); const params = { from: from.toISOString(), to: to.toISOString() }; if (this.campaignId) params.campaignId = this.campaignId; if (this.locale) params.locale = this.locale; ['contentId', 'device', 'template', 'placement', 'revision', 'actionId'].forEach(key => { if (this[key] !== '') params[key] = key === 'revision' ? Number(this[key]) : this[key] }); return params },
    async load() {
      this.sequence += 1; const { sequence } = this; this.loading = true; this.message = ''; this.report = null; this.nameSequence += 1
      try { const params = this.params(); const response = await axios.get('content-campaign-reports', { params, timeout: 10000 }); if (sequence !== this.sequence) return; if (!response.data || !Array.isArray(response.data.rows) || !response.data.totals || !response.data.freshness) throw new Error('INVALID_REPORT'); this.report = response.data; this.applied = params; this.reportPage = 1; this.hydrateNames() } catch (error) { if (sequence === this.sequence) this.message = this.failureText(error) } finally { if (sequence === this.sequence) this.loading = false }
    },
    async exportCsv() {
      if (this.loading || !this.applied || !this.canExport || !this.$can('export', 'contentCampaign')) return
      this.loading = true; this.message = ''; const { sequence } = this
      try { const response = await axios.get('content-campaign-reports/export', { params: this.applied, responseType: 'blob', timeout: 20000 }); if (sequence !== this.sequence) return; const url = URL.createObjectURL(new Blob([response.data], { type: 'text/csv;charset=utf-8' })); const link = document.createElement('a'); link.href = url; link.download = 'content-campaign-report.csv'; document.body.appendChild(link); link.click(); link.remove(); URL.revokeObjectURL(url) } catch (error) { if (sequence === this.sequence) this.message = this.failureText(error) } finally { if (sequence === this.sequence) this.loading = false }
    },
  },
}
</script>
